/**
 * ModuleTile — one study template in the Study catalog.
 *
 * Catalog tiles are *launchers*, not records, so they get a lighter
 * anatomy than <ResourceCard/>: a category-tinted icon tile, category
 * eyebrow, title, two-line blurb and a single "Run study" affordance.
 * The whole tile is the button. No gradient wash, no scale-on-hover,
 * no pill badges — colour comes from the tile and the eyebrow.
 */
import { ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { StatusBadge } from "@/components/StatusBadge";
import { TONE_TEXT, TONE_TILE, type Tone } from "@/lib/tone";
import { cn } from "@/lib/utils";

export type ModuleCategory = "Sales" | "Operations" | "Finance" | "Revenue" | "Data";

interface ModuleTileProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  category: ModuleCategory;
  recentlyUsed?: boolean;
  /** Tenant admin has switched this template off. Tile renders muted
   *  with an explanatory footer and ignores clicks. */
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
}

// One tone per category, applied to the icon tile and the eyebrow so
// a mixed grid scans by colour without becoming a rainbow of pills.
const CATEGORY_TONE: Record<ModuleCategory, Tone> = {
  Sales: "primary",
  Operations: "accent",
  Finance: "info",
  Revenue: "warning",
  Data: "success",
};

export const ModuleTile = ({
  title,
  description,
  icon,
  category,
  recentlyUsed,
  disabled = false,
  className,
  style,
  onClick,
}: ModuleTileProps) => {
  const clickable = Boolean(onClick) && !disabled;
  const tone = CATEGORY_TONE[category];
  return (
    <Card
      interactive={clickable}
      disabled={disabled}
      onClick={clickable ? onClick : undefined}
      aria-label={`${title} — ${category}`}
      className={cn("group flex min-h-[188px] flex-col p-5", className)}
      style={style}
    >
      <div className="flex items-start justify-between gap-3">
        <div
          className={cn(
            "flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border",
            "[&_svg]:h-[22px] [&_svg]:w-[22px]",
            TONE_TILE[tone],
          )}
        >
          {icon}
        </div>
        {recentlyUsed && <StatusBadge tone="info">Recent</StatusBadge>}
      </div>

      <div className="mt-4 min-w-0 flex-1">
        <div
          className={cn(
            "text-[11px] font-semibold uppercase tracking-wider",
            TONE_TEXT[tone],
          )}
        >
          {category}
        </div>
        <h3
          className={cn(
            "mt-1 text-base font-semibold leading-snug text-foreground transition-colors",
            clickable && "group-hover:text-primary",
          )}
        >
          {title}
        </h3>
        <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-muted-foreground">
          {description}
        </p>
      </div>

      <div className="mt-4 flex items-center justify-between text-xs">
        {disabled ? (
          <span className="text-muted-foreground">Disabled by your admin</span>
        ) : (
          <>
            <span />
            <span className="inline-flex items-center gap-1 font-semibold text-primary">
              Run study
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-150 group-hover:translate-x-0.5" />
            </span>
          </>
        )}
      </div>
    </Card>
  );
};
